import './teams.scss';

import SwapVertIcon from '@mui/icons-material/SwapVert';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EyeIcon from 'assets/images/iPrise/Eye.svg';
import { useDebounce } from 'hooks';
import { useStore } from 'mobx/StoreContext';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useCreateItem,
  useFetchList,
} from 'services/common/react-query-common-service';
import { useAppSelector } from 'store/hooks';
import { MASTER_ENDPOINT } from 'utils/Constant';

import StylishLoader from '../../common/components/StylishLoader';
import { TeamUserRoles } from '../enum/TeamUserRoles';
import CreateTeamModal from './components/CreateTeamModal';
import TeamFilterSidebar from './components/TeamFilterSidebar';
import { CreateTeamFormData } from './validations/createTeamValidation';

const Teams = observer(() => {
  const { userInfo: userDetail }: any = useAppSelector(
    (store: any) => store.userLogin,
  );
  const { managementTeamsService } = useStore();
  const { searchTerm, isCreateModalOpen, page, rowsPerPage } =
    managementTeamsService;
  const navigate = useNavigate();
  const isBusinessHead =
    userDetail?.teamUserRole === TeamUserRoles.BUSINESS_HEAD;
  const isManager = userDetail?.teamUserRole === TeamUserRoles.MANAGER;
  const isManagement = userDetail?.teamUserRole === TeamUserRoles.MANAGEMENT;
  const isTeamLead = userDetail?.teamUserRole === TeamUserRoles.TEAM_LEADER;
  const location = useLocation();

  const [localSearch, setLocalSearch] = useState(searchTerm);
  const debouncedSearch = useDebounce(localSearch, 500);

  useEffect(() => {
    managementTeamsService.setSearchTerm(debouncedSearch);
    managementTeamsService.setPage(0);
  }, [debouncedSearch, managementTeamsService]);

  // Build query params based on role
  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    params.append('skip', String(page * rowsPerPage));
    params.append('take', String(rowsPerPage));
    params.append('search', searchTerm);

    if (isBusinessHead && userDetail?.id) {
      params.append('businessHeadId', String(userDetail.id));
    } else if (isManager && userDetail?.id) {
      params.append('managerId', String(userDetail.id));
    } else if (isTeamLead && userDetail?.id) {
      params.append('teamLeadId', String(userDetail.id));
    }

    return params.toString();
  }, [
    page,
    rowsPerPage,
    searchTerm,
    isBusinessHead,
    isManager,
    isTeamLead,
    userDetail,
  ]);

  // Fetch teams list
  const {
    data: teamsResp,
    isLoading: isTeamsLoading,
    refetch: refetchTeams,
  } = useFetchList<any>(
    [
      'teams-list',
      page,
      rowsPerPage,
      searchTerm,
      isBusinessHead,
      isManager,
      isTeamLead,
    ],
    `${MASTER_ENDPOINT.Teams}?${String(queryParams)}`,
    {
      enabled: true,
      refetchOnMount: 'always', // Refetch on every mount
    },
  );

  const teams = useMemo(() => teamsResp?.data || [], [teamsResp]);
  const totalCount = teamsResp?.total || 0;

  const { mutateAsync: createTeam } = useCreateItem();

  const handleOpenCreateModal = () => {
    managementTeamsService.setIsCreateModalOpen(true);
  };

  const handleCloseCreateModal = () => {
    managementTeamsService.setIsCreateModalOpen(false);
  };

  const handleTeamSubmit = async (data: CreateTeamFormData) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { customerIds, teamCustomers, ...restData } = data;
    const payload = {
      ...restData,
      teamCustomers: teamCustomers || [],
      workingHoursTimezone: 'America/New_York', // Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    await createTeam({
      data: payload,
      endpoint: MASTER_ENDPOINT.Teams,
    });
    handleCloseCreateModal();
    refetchTeams();
  };

  const handleViewTeam = (team: any) => {
    managementTeamsService.setTeamInfo(team);
    // Get base path from current location (e.g., /management, /manager, /business-head)
    const basePath = location.pathname.split('/')[1];
    navigate(`/${basePath}/team/${String(team.id)}`);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    managementTeamsService.setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    managementTeamsService.setRowsPerPage(parseInt(event.target.value, 10));
    managementTeamsService.setPage(0);
  };

  return (
    <Box className="teams-container">
      {/* Top Bar: Title, Search, Actions */}
      <Box className="teams-header">
        <Typography className="teams-title">
          {isManagement ? 'Manage Team' : 'My Team'}
        </Typography>
        <Box className="teams-actions">
          <TextField
            placeholder="Search"
            size="small"
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
            className="search-field"
          />
          <Button className="sort-button" disableRipple>
            <SwapVertIcon sx={{ color: '#5e6c84', fontSize: '18px' }} />
          </Button>
          {isManagement && (
            <Button
              variant="contained"
              disableElevation
              className="create-team-btn"
              onClick={handleOpenCreateModal}>
              Create Team
            </Button>
          )}
        </Box>
      </Box>

      {/* Content Area: Table & Pagination Combined */}
      <Box className="teams-content-wrapper">
        <Box className="teams-table-wrapper">
          <TableContainer className="teams-table-container">
            <Table stickyHeader className="teams-table">
              <TableHead className="table-header">
                <TableRow>
                  {[
                    'Code',
                    'Name',
                    'Job Source',
                    'Business Head',
                    'Manager',
                    'Members',
                    'Status',
                    'Actions',
                  ].map((head) => (
                    <TableCell
                      key={head}
                      className="header-cell"
                      align={head === 'Actions' ? 'center' : 'left'}>
                      {head}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody className="table-body">
                {isTeamsLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <StylishLoader mode="table" text="Loading teams..." />
                    </TableCell>
                  </TableRow>
                ) : teams.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography
                        sx={{
                          color: '#5e6c84',
                          fontFamily: 'Inter',
                          fontSize: '14px',
                          fontWeight: 500,
                          pt: 5,
                        }}>
                        No teams found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  teams.map((row: any) => (
                    <TableRow key={row.id} className="table-row">
                      <TableCell className="body-cell">
                        {row.teamCode || row.code}
                      </TableCell>
                      <TableCell className="body-cell">
                        {row.teamName || row.name}
                      </TableCell>
                      <TableCell
                        className="body-cell"
                        sx={{ maxWidth: '200px' }}>
                        {(() => {
                          if (
                            !Array.isArray(row.teamJobSources) ||
                            row.teamJobSources.length === 0
                          ) {
                            return row.jobSource || '-';
                          }

                          const customerNamesStr =
                            Array.isArray(row.teamCustomers) &&
                            row.teamCustomers.length > 0
                              ? row.teamCustomers
                                  .map((c: any) =>
                                    String(c.customer?.name || c.name || ''),
                                  )
                                  .filter(Boolean)
                                  .join(', ')
                              : '';

                          const jobSourceNames = row.teamJobSources
                            .map((c: any) => {
                              const name = String(
                                c.jobSource?.name || c.name || '',
                              );
                              if (
                                name.toLowerCase() === 'beeline' &&
                                customerNamesStr
                              ) {
                                return `${String(name)} (${String(
                                  customerNamesStr,
                                )})`;
                              }
                              return name;
                            })
                            .filter(Boolean);

                          const firstJobSource = jobSourceNames[0] || '';
                          const remainingCount = jobSourceNames.length - 1;
                          const allJobSources = jobSourceNames.join(', ');

                          if (jobSourceNames.length === 1) {
                            return (
                              <Tooltip
                                title={firstJobSource}
                                placement="top"
                                arrow
                                slotProps={{
                                  arrow: {
                                    sx: {
                                      '&::before': {
                                        border: '1px solid #e3e6ea',
                                      },
                                      color: '#fff',
                                    },
                                  },
                                  tooltip: {
                                    sx: {
                                      backgroundColor: '#fff',
                                      border: '1px solid #e3e6ea',
                                      borderRadius: '6px',
                                      boxShadow:
                                        '0px 2px 8px rgba(0, 0, 0, 0.15)',
                                      color: '#424242',
                                      fontFamily: 'Inter',
                                      fontSize: '12px',
                                      lineHeight: 1.6,
                                      padding: '8px 12px',
                                    },
                                  },
                                }}>
                                <Box
                                  sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}>
                                  {firstJobSource}
                                </Box>
                              </Tooltip>
                            );
                          }

                          return (
                            <Tooltip
                              title={allJobSources}
                              placement="top"
                              arrow
                              slotProps={{
                                arrow: {
                                  sx: {
                                    '&::before': {
                                      border: '1px solid #e3e6ea',
                                    },
                                    color: '#fff',
                                  },
                                },
                                tooltip: {
                                  sx: {
                                    backgroundColor: '#fff',
                                    border: '1px solid #e3e6ea',
                                    borderRadius: '6px',
                                    boxShadow:
                                      '0px 2px 8px rgba(0, 0, 0, 0.15)',
                                    color: '#424242',
                                    fontFamily: 'Inter',
                                    fontSize: '12px',
                                    lineHeight: 1.6,
                                    padding: '8px 12px',
                                  },
                                },
                              }}>
                              <Box
                                sx={{
                                  alignItems: 'center',
                                  display: 'flex',
                                  gap: 0.5,
                                }}>
                                <Box
                                  sx={{
                                    flex: 1,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}>
                                  {firstJobSource}
                                </Box>
                                <Chip
                                  label={`+${remainingCount} more`}
                                  size="small"
                                  sx={{
                                    '& .MuiChip-label': {
                                      padding: '0 6px',
                                    },
                                    backgroundColor: '#f4f5f7',
                                    color: '#5e6c84',
                                    fontFamily: 'Inter',
                                    fontSize: '11px',
                                    height: '20px',
                                  }}
                                />
                              </Box>
                            </Tooltip>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="body-cell">
                        {row.businessHead?.firstName
                          ? `${String(row.businessHead.firstName)} ${String(
                              row.businessHead.lastName,
                            )}`
                          : row.businessHead}
                      </TableCell>
                      <TableCell className="body-cell">
                        {row.teamManager?.firstName
                          ? `${String(row.teamManager.firstName)} ${String(
                              row.teamManager.lastName,
                            )}`
                          : row.manager}
                      </TableCell>
                      <TableCell className="body-cell">
                        {row.membersCount || row.members || 0}
                      </TableCell>
                      <TableCell className="body-cell">
                        <Chip
                          label={row.isActive ? 'Active' : 'Inactive'}
                          className={`status-chip ${
                            !row.isActive ? 'inactive' : ''
                          }`}
                        />
                      </TableCell>
                      <TableCell align="center" className="body-cell">
                        <IconButton
                          size="small"
                          className="action-icon-button"
                          onClick={() => handleViewTeam(row)}>
                          <img
                            src={EyeIcon}
                            alt="View"
                            className="action-icon"
                          />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Right Side Filter Sidebar */}
          <TeamFilterSidebar />
        </Box>

        {/* Material-UI TablePagination */}
        <TablePagination
          className="teams-pagination"
          component="div"
          count={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[50, 100, 150, 200, 250]}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Box>

      {/* Create Team Modal */}
      <CreateTeamModal
        open={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        onSubmit={handleTeamSubmit}
      />
    </Box>
  );
});

export default Teams;
